import React from 'react';
import Head from 'next/head';
import { useHooks, TabType } from '@/pages/hooks';
import MetricsBar from '@/components/templates/MetricsBar';
import ImportPanel from '@/components/templates/ImportPanel';
import HrxSyncPanel from '@/components/templates/HrxSyncPanel';
import DailyLog from '@/components/templates/DailyLog';
import WeeklyChart from '@/components/templates/WeeklyChart';
import MonthlyChart from '@/components/templates/MonthlyChart';
import PayCalculator from '@/components/templates/PayCalculator';
import DayEditModal from '@/components/templates/DayEditModal';

const DashboardPage: React.FC = () => {
  const {
    activeTab,
    setActiveTab,
    dayMap,
    sessions,
    overrides,
    darkMode,
    toggleDarkMode,
    isHydrated,
    isAuthenticated,
    isNewPasscode,
    pendingPasscode,
    activePasscode,
    handleLogin,
    handleSignUp,
    cancelSignUp,
    handleLogout,
    handleSaveOverride,
    handleDeleteOverride,
    rateType,
    dailyRate,
    hourlyRate,
    handleUpdateRates,
    entries,
    handleImportEntries,
    handleClearAllEntries,
    handleSyncFromHrx
  } = useHooks();

  const [password, setPassword] = React.useState('');
  const [error, setError] = React.useState(false);

  // Edit Day modal state variables
  const [isEditModalOpen, setIsEditModalOpen] = React.useState(false);
  const [editModalDate, setEditModalDate] = React.useState('');
  const [isEditModalNew, setIsEditModalNew] = React.useState(false);
  const [editModalInitialHours, setEditModalInitialHours] = React.useState(0);
  const [editModalInitialStatus, setEditModalInitialStatus] = React.useState('');

  const [showApology, setShowApology] = React.useState(false);

  React.useEffect(() => {
    if (typeof window !== 'undefined') {
      const dismissed = localStorage.getItem('hours_parser_apology_dismissed');
      if (!dismissed) {
        setShowApology(true);
      }
    }
  }, []);

  const handleDismissApology = () => {
    localStorage.setItem('hours_parser_apology_dismissed', 'true');
    setShowApology(false);
  };

  const openEditModal = (dateStr: string) => {
    setEditModalDate(dateStr);
    setIsEditModalNew(false);
    const currentHours = dayMap[dateStr] || 0;
    const currentStatus = overrides[dateStr]?.status || '';
    setEditModalInitialHours(currentHours);
    setEditModalInitialStatus(currentStatus);
    setIsEditModalOpen(true);
  };

  const openAddManualModal = () => {
    const today = new Date();
    const yyyy = today.getFullYear();
    const mm = String(today.getMonth() + 1).padStart(2, '0');
    const dd = String(today.getDate()).padStart(2, '0');
    setEditModalDate(`${yyyy}-${mm}-${dd}`);
    setIsEditModalNew(true);
    setEditModalInitialHours(8);
    setEditModalInitialStatus('Present');
    setIsEditModalOpen(true);
  };

  const handleSaveModal = (dateStr: string, hoursVal: number, statusVal: string) => {
    handleSaveOverride(dateStr, hoursVal, statusVal);
    setIsEditModalOpen(false);
  };

  const handleDeleteModal = (dateStr: string) => {
    handleDeleteOverride(dateStr);
    setIsEditModalOpen(false);
  };

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const result = await handleLogin(password);
    if (result.success) {
      setError(false);
      setPassword('');
    } else {
      if (!result.isNew) {
        setError(true);
      }
    }
  };

  if (!isHydrated) {
    return (
      <div className="min-h-screen bg-[#080808] flex items-center justify-center font-mono text-xs text-zinc-600">
        LOADING INSTRUMENTS...
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <>
        <Head>
          <title>Access Control // Timelog Tracker</title>
        </Head>
        <div className="min-h-screen bg-[#080808] flex flex-col items-center justify-center px-4 py-8 space-y-6">
          {showApology && (
            <div className="w-full max-w-md bg-accent/5 border border-accent/20 rounded p-4 relative overflow-hidden animate-fade-in shadow-lg">
              <div className="absolute top-0 left-0 bottom-0 w-[4px] bg-accent"></div>
              <div className="pl-2 pr-6">
                <div className="flex items-center gap-2 mb-1.5">
                  <svg className="h-4 w-4 text-accent" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                  </svg>
                  <h3 className="text-xs font-mono uppercase tracking-wider text-accent font-semibold">
                    NEW FEATURE // HRX DIRECT SYNC
                  </h3>
                </div>
                <p className="text-[11px] font-sans text-foreground/80 leading-relaxed mb-2">
                  You can now pull DTR sessions straight from <span className="font-mono text-accent">api-hrx.arrowsoft.cloud</span> instead of pasting the timesheet text. Here's how to grab the access token you'll need:
                </p>
                <ol className="text-[11px] font-sans text-foreground/80 leading-relaxed space-y-1.5 list-decimal pl-5 mb-2">
                  <li>
                    Log into HRX at{' '}
                    <a
                      href="https://hrx.arrowsoft.cloud"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="font-mono text-accent underline hover:text-accent/80"
                    >
                      hrx.arrowsoft.cloud
                    </a>{' '}
                    (opens in a new tab — any browser tab is fine, even incognito).
                  </li>
                  <li>Open DevTools with <kbd className="font-mono text-[10px] bg-background border border-card-border px-1 py-0.5 rounded">F12</kbd> (or right-click → Inspect).</li>
                  <li>Go to <span className="font-mono text-accent">Application</span> → <span className="font-mono text-accent">Cookies</span> → <span className="font-mono text-accent">https://api-hrx.arrowsoft.cloud</span>.</li>
                  <li>Find <code className="font-mono text-accent">accessToken</code>, double-click its Value, copy.</li>
                  <li>Paste it into the <span className="font-mono text-accent">HRX accessToken (Cookie)</span> field on the <span className="font-mono text-accent">Import logs</span> tab below.</li>
                </ol>
                <p className="text-[11px] font-sans text-foreground/60 leading-relaxed">
                  Token expires in 8 hours. Hit <span className="font-mono text-accent">Save to cache</span> after pasting so the next sync uses the same token without re-pasting. The token never leaves the timelog DB — it's scoped per passcode.
                </p>
                <button
                  onClick={handleDismissApology}
                  className="absolute top-3 right-3 text-foreground/40 hover:text-foreground transition-all cursor-pointer focus:outline-none"
                  title="Dismiss Notice"
                >
                  <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>
          )}

          <div className="w-full max-w-sm glass-panel p-8 space-y-6 border border-card-border bg-card-bg rounded relative overflow-hidden animate-fade-in">
            {/* Top decorative accent stripe */}
            <div className="absolute top-0 left-0 right-0 h-[2px] bg-accent"></div>
            
            {isNewPasscode ? (
              <div className="space-y-6 animate-fade-in">
                <div className="text-center space-y-2">
                  <div className="mx-auto h-10 w-10 rounded-full border border-card-border flex items-center justify-center bg-background/50 mb-2">
                    <svg className="h-4 w-4 text-accent animate-pulse" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z" />
                    </svg>
                  </div>
                  <h2 className="text-md font-display font-semibold tracking-tight text-foreground">
                    REGISTER PROFILE
                  </h2>
                  <p className="text-[9px] text-foreground/40 font-mono tracking-widest uppercase">
                    NEW PASSCODE IDENTIFIED
                  </p>
                </div>

                <div className="space-y-4">
                  <p className="text-[11px] text-foreground/60 font-mono text-center leading-relaxed">
                    THE PASSCODE <span className="text-accent font-semibold">&ldquo;{pendingPasscode}&rdquo;</span> IS NOT REGISTERED. WOULD YOU LIKE TO CREATE A NEW TIMELOG ACCOUNT WITH THIS PASSCODE?
                  </p>

                  <div className="flex flex-col gap-2 pt-2">
                    <button
                      onClick={handleSignUp}
                      className="w-full bg-accent text-foreground font-mono text-[10px] uppercase tracking-wider font-semibold py-2.5 px-4 rounded hover:bg-accent/90 transition-all cursor-pointer text-center"
                    >
                      Create Profile & Login
                    </button>
                    <button
                      onClick={cancelSignUp}
                      className="w-full border border-card-border text-foreground/60 hover:text-foreground font-mono text-[10px] uppercase tracking-wider py-2.5 px-4 rounded bg-background/10 hover:bg-background/20 transition-all cursor-pointer text-center"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              </div>
            ) : (
              <>
                <div className="text-center space-y-2">
                  <div className="mx-auto h-10 w-10 rounded-full border border-card-border flex items-center justify-center bg-background/50 mb-2">
                    <svg className="h-4 w-4 text-accent" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                    </svg>
                  </div>
                  <h2 className="text-md font-display font-semibold tracking-tight text-foreground">
                    ACCESS SYSTEM LOCK
                  </h2>
                  <p className="text-[9px] text-foreground/40 font-mono tracking-widest uppercase">
                    TIMELOG TRACKER SECURITY GATE
                  </p>
                </div>

                <form onSubmit={onSubmit} className="space-y-4">
                  <div className="space-y-1.5">
                    <label className="text-[9px] text-foreground/50 font-mono uppercase tracking-wider block">
                      Instrument Passcode
                    </label>
                    <input
                      type="password"
                      value={password}
                      onChange={(e) => {
                        setPassword(e.target.value);
                        if (error) setError(false);
                      }}
                      className="w-full bg-background/30 border border-card-border rounded px-3 py-2 text-xs font-mono focus:outline-none focus:border-accent text-foreground placeholder:text-foreground/15 transition-all text-center tracking-widest"
                      placeholder="••••••••••••"
                      autoFocus
                    />
                  </div>

                  {error && (
                    <div className="text-[9px] font-mono uppercase text-red-400/80 tracking-wider bg-red-950/10 border border-red-900/20 rounded py-2 text-center">
                      ACCESS DENIED: INVALID PASSCODE
                    </div>
                  )}

                  <button
                    type="submit"
                    className="w-full bg-foreground text-background font-mono text-[10px] uppercase tracking-wider font-semibold py-2.5 px-4 rounded hover:bg-accent hover:text-foreground transition-all cursor-pointer"
                  >
                    Authenticate Instrument
                  </button>
                </form>
              </>
            )}
            
            <div className="text-center pt-2">
              <p className="text-[8px] text-foreground/25 font-mono uppercase tracking-wider">
                DECRYPTING AES LOCAL STORAGE DATA ON DEPLOY
              </p>
            </div>
          </div>
        </div>
      </>
    );
  }

  const tabLabels: { key: TabType; label: string }[] = [
    { key: 'import', label: 'Import logs' },
    { key: 'log', label: 'Daily log' },
    { key: 'weekly', label: 'Weekly charts' },
    { key: 'monthly', label: 'Monthly charts' },
    { key: 'pay', label: 'Pay Calculator' },
  ];

  return (
    <>
      <Head>
        <title>Timelog Tracker // Industrial Instrument</title>
        <meta name="description" content="Personal timelog dashboard and hours parsed aggregator." />
      </Head>

      <div className="min-h-screen pb-16 transition-colors duration-300">
        <header className="border-b border-card-border py-6 mb-8 bg-background/20 backdrop-blur-md sticky top-0 z-50">
          <div className="max-w-4xl mx-auto px-4 flex justify-between items-center">
            <div className="space-y-1">
              <h1 className="text-2xl font-display font-bold tracking-tight text-foreground">
                TIMELOG TRACKER
              </h1>
              <p className="text-[10px] text-foreground/40 font-mono tracking-widest uppercase">
                SYSTEM AGGREGATOR // OFFLINE INSTRUMENT v1.0 // ACTIVE: {activePasscode === 'torrejos' ? 'DEFAULT' : activePasscode.toUpperCase()}
              </p>
            </div>
            
            <div className="flex items-center gap-2">
              {/* Dark Mode Toggle */}
              <button
                onClick={toggleDarkMode}
                className="p-2 border border-card-border hover:border-foreground/20 rounded bg-background/30 text-foreground/75 hover:text-foreground transition-all focus:outline-none cursor-pointer"
                title="Toggle Theme Mode"
              >
                {darkMode ? (
                  <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364-6.364l-.707.707M6.343 17.657l-.707.707m0-12.728l.707.707m12.728 12.728l.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" />
                  </svg>
                ) : (
                  <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" />
                  </svg>
                )}
              </button>

              {/* Lock App Button */}
              <button
                onClick={handleLogout}
                className="p-2 border border-card-border hover:border-foreground/20 rounded bg-background/30 text-foreground/75 hover:text-foreground transition-all focus:outline-none cursor-pointer"
                title="Lock Application"
              >
                <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                </svg>
              </button>
            </div>
          </div>
        </header>

        <main className="max-w-4xl mx-auto px-4 space-y-8">
          {/* Feature announcement — HRX Direct Sync tutorial */}
          {showApology && (
            <div className="bg-accent/5 border border-accent/20 rounded p-4 relative overflow-hidden animate-fade-in">
              <div className="absolute top-0 left-0 bottom-0 w-[4px] bg-accent"></div>
              <div className="pl-2 pr-6">
                <div className="flex items-center gap-2 mb-1.5">
                  <svg className="h-4 w-4 text-accent" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                  </svg>
                  <h3 className="text-xs font-mono uppercase tracking-wider text-accent font-semibold">
                    NEW FEATURE // HRX DIRECT SYNC — HOW TO GET YOUR ACCESS TOKEN
                  </h3>
                </div>
                <p className="text-[11px] font-sans text-foreground/80 leading-relaxed mb-2">
                  Scroll down to the <span className="font-mono text-accent">Sync from HRX</span> panel. You'll need an 8-hour JWT to authenticate. Here's how to grab one:
                </p>
                <ol className="text-[11px] font-sans text-foreground/80 leading-relaxed space-y-1.5 list-decimal pl-5 mb-2">
                  <li>
                    Log into HRX at{' '}
                    <a
                      href="https://hrx.arrowsoft.cloud"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="font-mono text-accent underline hover:text-accent/80"
                    >
                      hrx.arrowsoft.cloud
                    </a>{' '}
                    (opens in a new tab — any browser tab, even incognito works).
                  </li>
                  <li>Open DevTools with <kbd className="font-mono text-[10px] bg-background border border-card-border px-1 py-0.5 rounded">F12</kbd> (or right-click → Inspect).</li>
                  <li>Go to <span className="font-mono text-accent">Application</span> → <span className="font-mono text-accent">Cookies</span> → <span className="font-mono text-accent">https://api-hrx.arrowsoft.cloud</span>.</li>
                  <li>Find the cookie named <code className="font-mono text-accent">accessToken</code>, double-click its Value, copy.</li>
                  <li>Paste it into <span className="font-mono text-accent">HRX accessToken (Cookie)</span>, then click <span className="font-mono text-accent">Save to cache</span>.</li>
                </ol>
                <p className="text-[11px] font-sans text-foreground/60 leading-relaxed">
                  Once cached, syncs use the same token until it expires (8h). The token is stored per-passcode in the timelog DB — never leaves the server.
                </p>
                <button
                  onClick={handleDismissApology}
                  className="absolute top-3 right-3 text-foreground/40 hover:text-foreground transition-all cursor-pointer focus:outline-none"
                  title="Dismiss Notice"
                >
                  <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>
          )}

          {/* Metrics bar */}
          <MetricsBar dayMap={dayMap} />

          {/* Navigation Tabs */}
          <div className="border-b border-card-border flex items-center gap-1 overflow-x-auto no-scrollbar">
            {tabLabels.map((tab) => (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                className={`px-5 py-3 text-xs uppercase font-mono tracking-wider border-b-2 transition-all shrink-0 focus:outline-none cursor-pointer ${
                  activeTab === tab.key
                    ? 'border-accent text-accent font-semibold'
                    : 'border-transparent text-foreground/45 hover:text-foreground/80'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>

          {/* Tab contents */}
          <div className="min-h-[300px]">
            {activeTab === 'import' && (
              <>
                <ImportPanel
                  entries={entries}
                  onImport={handleImportEntries}
                  onClearAll={handleClearAllEntries}
                />
                <div className="mt-6">
                  <HrxSyncPanel
                    onSync={handleSyncFromHrx}
                    defaultPersonnelId="cmqege8ax00106nog4v2x0bw2"
                  />
                </div>
              </>
            )}
            
            {activeTab === 'log' && (
              <DailyLog
                sessions={sessions}
                dayMap={dayMap}
                overrides={overrides}
                onEditDay={openEditModal}
                onAddManualDay={openAddManualModal}
              />
            )}
            
            {activeTab === 'weekly' && (
              <WeeklyChart
                dayMap={dayMap}
              />
            )}
            
            {activeTab === 'monthly' && (
              <MonthlyChart
                dayMap={dayMap}
              />
            )}

            {activeTab === 'pay' && (
              <PayCalculator
                dayMap={dayMap}
                rateType={rateType}
                dailyRate={dailyRate}
                hourlyRate={hourlyRate}
                onUpdateRates={handleUpdateRates}
                passcode={activePasscode}
              />
            )}
          </div>
        </main>
      </div>

      <DayEditModal
        key={isEditModalOpen ? `edit-${editModalDate}-${editModalInitialHours}-${editModalInitialStatus}` : 'closed'}
        isOpen={isEditModalOpen}
        date={editModalDate}
        isNew={isEditModalNew}
        initialHours={editModalInitialHours}
        initialStatus={editModalInitialStatus}
        onSave={handleSaveModal}
        onDelete={handleDeleteModal}
        onClose={() => setIsEditModalOpen(false)}
      />
    </>
  );
};

export default DashboardPage;
